import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import useAdminAutoRefresh from './useAdminAutoRefresh';
import './AdminUsers.css';
import './AdminSessions.css';

const AUTO_REFRESH_INTERVAL_MS = 20000;

const EVENT_TYPE_OPTIONS = [
  'admin_note_added',
  'credits_added',
  'credits_removed',
  'creator_studio_launched',
  'credit_correction',
  'feature_flag_updated',
  'payment_issue_flagged',
  'payment_reviewed',
  'settings_updated',
  'user_disabled',
  'user_marked_internal',
  'user_marked_internal_test',
  'user_reenabled',
  'user_unmarked_internal',
  'user_unmarked_internal_test',
];

function formatDateTime(value) {
  if (!value) {
    return 'Not available';
  }

  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) {
    return 'Not available';
  }

  return date.toLocaleString([], {
    dateStyle: 'medium',
    timeStyle: 'short',
  });
}

function truncate(value, maxLength = 64) {
  if (!value) {
    return 'None';
  }

  return value.length > maxLength ? `${value.slice(0, maxLength)}…` : value;
}

function badgeClassName(value) {
  if (value.includes('disabled') || value.includes('removed') || value.includes('flagged')) {
    return 'cq-admin-badge cq-admin-badge--disabled';
  }

  if (value.includes('launched') || value.includes('added') || value.includes('reenabled') || value.includes('updated') || value.includes('marked')) {
    return 'cq-admin-badge cq-admin-badge--active';
  }

  return 'cq-admin-badge cq-admin-badge--neutral';
}

function getTargetLink(record) {
  const targetType = String(record.target_entity_type || '').toLowerCase();
  const targetId = record.target_entity_id;

  if (!targetId) {
    return null;
  }

  if (targetType === 'user') {
    return `/admin/users/${targetId}`;
  }

  if (targetType === 'session' || targetType === 'game_session') {
    return `/admin/sessions/${targetId}`;
  }

  if (targetType === 'payment' || targetType === 'credit_transaction' || targetType === 'transaction') {
    return '/admin/payments';
  }

  if (targetType === 'game_pack' || targetType === 'quest_pack' || targetType === 'content') {
    return '/admin/content';
  }

  return null;
}

const AdminAuditLogPage = () => {
  const [filters, setFilters] = useState({
    search: '',
    range: '7d',
    admin_user_id: '',
    target_entity_type: '',
    target_entity_id: '',
    has_reason: '',
    page: 1,
  });
  const [selectedEventTypes, setSelectedEventTypes] = useState([]);
  const [expandedRecordId, setExpandedRecordId] = useState('');
  const [state, setState] = useState({
    loading: true,
    refreshing: false,
    error: '',
    records: [],
    pagination: {
      page: 1,
      total_pages: 1,
      total_count: 0,
    },
    lastUpdatedAt: null,
  });
  const { refreshNonce, triggerRefresh } = useAdminAutoRefresh({ intervalMs: AUTO_REFRESH_INTERVAL_MS });

  useEffect(() => {
    const token = localStorage.getItem('auth_token');
    const controller = new AbortController();

    const loadAuditLog = async ({ background = false } = {}) => {
      setState((current) => ({
        ...current,
        loading: background ? current.loading : true,
        refreshing: background,
        error: '',
      }));

      try {
        const response = await fetch('/.netlify/functions/admin-get-audit-log', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            ...filters,
            event_type: selectedEventTypes,
            page_size: 25,
          }),
          signal: controller.signal,
        });

        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(data?.message || 'Unable to load audit log.');
        }

        setState({
          loading: false,
          refreshing: false,
          error: '',
          records: data.records || [],
          pagination: data.pagination || {
            page: 1,
            total_pages: 1,
            total_count: 0,
          },
          lastUpdatedAt: new Date().toISOString(),
        });
      } catch (error) {
        if (controller.signal.aborted) {
          return;
        }

        setState({
          loading: false,
          refreshing: false,
          error: error.message || 'Unable to load audit log.',
          records: [],
          pagination: {
            page: 1,
            total_pages: 1,
            total_count: 0,
          },
          lastUpdatedAt: null,
        });
      }
    };

    loadAuditLog({ background: refreshNonce > 0 });

    return () => controller.abort();
  }, [filters, refreshNonce, selectedEventTypes]);

  return (
    <section className="cq-admin-page">
      <header className="cq-admin-page__header">
        <div>
          <p className="cq-admin-page__eyebrow">Operational Accountability</p>
          <h2 className="cq-admin-page__title">Audit Log</h2>
          <p className="cq-admin-page__copy">Append-only record of meaningful admin actions across users, credits, Creator Studio, and operations.</p>
        </div>
        <div className="cq-admin-page__actions">
          <div className="cq-admin-page__actions-row">
            <button
              type="button"
              className="cq-admin-action-button cq-admin-action-button--secondary"
              disabled={state.loading || state.refreshing}
              onClick={triggerRefresh}
            >
              Refresh
            </button>
          </div>
          <p className="cq-admin-page__meta">
            {state.refreshing ? 'Refreshing...' : `Last updated ${formatDateTime(state.lastUpdatedAt)}`}
          </p>
        </div>
      </header>

      <section className="cq-admin-panel cq-admin-audit-filters-grid">
        <label className="cq-admin-field">
          <span>Search</span>
          <input
            className="cq-admin-input"
            value={filters.search}
            onChange={(event) => setFilters((current) => ({ ...current, search: event.target.value, page: 1 }))}
          />
        </label>
        <label className="cq-admin-field">
          <span>Date Range</span>
          <select
            className="cq-admin-input"
            value={filters.range}
            onChange={(event) => setFilters((current) => ({ ...current, range: event.target.value, page: 1 }))}
          >
            <option value="today">Today</option>
            <option value="7d">Last 7 Days</option>
            <option value="30d">Last 30 Days</option>
            <option value="all">All Time</option>
          </select>
        </label>
        <label className="cq-admin-field">
          <span>Admin User ID</span>
          <input
            className="cq-admin-input"
            value={filters.admin_user_id}
            onChange={(event) => setFilters((current) => ({ ...current, admin_user_id: event.target.value, page: 1 }))}
          />
        </label>
        <label className="cq-admin-field">
          <span>Target Entity Type</span>
          <input
            className="cq-admin-input"
            value={filters.target_entity_type}
            onChange={(event) => setFilters((current) => ({ ...current, target_entity_type: event.target.value, page: 1 }))}
          />
        </label>
        <label className="cq-admin-field">
          <span>Target Entity ID</span>
          <input
            className="cq-admin-input"
            value={filters.target_entity_id}
            onChange={(event) => setFilters((current) => ({ ...current, target_entity_id: event.target.value, page: 1 }))}
          />
        </label>
        <label className="cq-admin-field">
          <span>Has Reason</span>
          <select
            className="cq-admin-input"
            value={filters.has_reason}
            onChange={(event) => setFilters((current) => ({ ...current, has_reason: event.target.value, page: 1 }))}
          >
            <option value="">All</option>
            <option value="true">Yes</option>
            <option value="false">No</option>
          </select>
        </label>
        <label className="cq-admin-field cq-admin-field--full">
          <span>Event Types</span>
          <select
            className="cq-admin-input cq-admin-input--multiselect"
            multiple
            value={selectedEventTypes}
            onChange={(event) => {
              const values = Array.from(event.target.selectedOptions).map((option) => option.value);
              setSelectedEventTypes(values);
              setFilters((current) => ({ ...current, page: 1 }));
            }}
          >
            {EVENT_TYPE_OPTIONS.map((eventType) => (
              <option key={eventType} value={eventType}>{eventType}</option>
            ))}
          </select>
        </label>
      </section>

      {state.loading ? <div className="cq-admin-empty-state"><p className="cq-admin-empty-state__title">Loading audit log...</p></div> : null}
      {!state.loading && state.error ? <div className="cq-admin-empty-state"><p className="cq-admin-empty-state__title">Unable to load audit log.</p><p className="cq-admin-empty-state__copy">{state.error}</p></div> : null}
      {!state.loading && !state.error && !state.records.length ? <div className="cq-admin-empty-state"><p className="cq-admin-empty-state__title">No audit records found.</p></div> : null}

      {!state.loading && !state.error && state.records.length ? (
        <section className="cq-admin-panel">
          <div className="cq-admin-table-wrap">
            <table className="cq-admin-table">
              <thead>
                <tr>
                  <th>Created At</th>
                  <th>Event Type</th>
                  <th>Admin</th>
                  <th>Target Entity Type</th>
                  <th>Target Entity ID</th>
                  <th>Reason</th>
                </tr>
              </thead>
              <tbody>
                {state.records.map((record) => {
                  const isExpanded = expandedRecordId === record.id;
                  const targetLink = getTargetLink(record);

                  return (
                    <React.Fragment key={record.id}>
                      <tr
                        className="cq-admin-audit-row"
                        onClick={() => setExpandedRecordId((current) => (current === record.id ? '' : record.id))}
                      >
                        <td>{formatDateTime(record.created_at)}</td>
                        <td><span className={badgeClassName(record.event_type)}>{record.event_type}</span></td>
                        <td>{record.admin_email || record.admin_user_id || 'Unknown'}</td>
                        <td>{record.target_entity_type || 'None'}</td>
                        <td className="cq-admin-cell-id">{truncate(record.target_entity_id, 24)}</td>
                        <td>{truncate(record.reason, 72)}</td>
                      </tr>
                      {isExpanded ? (
                        <tr className="cq-admin-audit-expanded-row">
                          <td colSpan={6}>
                            <div className="cq-admin-users-grid cq-admin-users-grid--actions">
                              <section>
                                <h3 className="cq-admin-section-title">Event Details</h3>
                                <dl className="cq-admin-definition-list">
                                  <div><dt>Event Type</dt><dd>{record.event_type}</dd></div>
                                  <div><dt>Created At</dt><dd>{formatDateTime(record.created_at)}</dd></div>
                                  <div><dt>Admin User ID</dt><dd className="cq-admin-cell-id">{record.admin_user_id || 'None'}</dd></div>
                                  <div><dt>Admin Email</dt><dd>{record.admin_email || 'None'}</dd></div>
                                  <div><dt>Target Entity Type</dt><dd>{record.target_entity_type || 'None'}</dd></div>
                                  <div><dt>Target Entity ID</dt><dd className="cq-admin-cell-id">{record.target_entity_id || 'None'}</dd></div>
                                  <div><dt>Reason</dt><dd>{record.reason || 'None'}</dd></div>
                                  <div><dt>Related Link</dt><dd>{targetLink ? <Link className="cq-admin-card-link" to={targetLink}>Open related record</Link> : 'Not available'}</dd></div>
                                </dl>
                              </section>
                              <section className="cq-admin-embedded-studio">
                                <div>
                                  <h3 className="cq-admin-section-title">Before Value</h3>
                                  <pre className="cq-admin-pre">{JSON.stringify(record.before_value || {}, null, 2)}</pre>
                                </div>
                                <div>
                                  <h3 className="cq-admin-section-title">After Value</h3>
                                  <pre className="cq-admin-pre">{JSON.stringify(record.after_value || {}, null, 2)}</pre>
                                </div>
                                <div>
                                  <h3 className="cq-admin-section-title">Metadata</h3>
                                  <pre className="cq-admin-pre">{JSON.stringify(record.metadata || {}, null, 2)}</pre>
                                </div>
                              </section>
                            </div>
                          </td>
                        </tr>
                      ) : null}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
      ) : null}

      {!state.loading && !state.error ? (
        <div className="cq-admin-session-pagination">
          <button
            type="button"
            className="cq-admin-action-button cq-admin-action-button--secondary"
            disabled={state.pagination.page <= 1}
            onClick={() => setFilters((current) => ({ ...current, page: current.page - 1 }))}
          >
            Previous
          </button>
          <span>Page {state.pagination.page} of {state.pagination.total_pages}</span>
          <button
            type="button"
            className="cq-admin-action-button cq-admin-action-button--secondary"
            disabled={state.pagination.page >= state.pagination.total_pages}
            onClick={() => setFilters((current) => ({ ...current, page: current.page + 1 }))}
          >
            Next
          </button>
        </div>
      ) : null}
    </section>
  );
};

export default AdminAuditLogPage;
